import ReactApexChart from 'react-apexcharts'
import { getAlarmModalChartData, getDailyAlarmModalChartData } from './core/_requests'
import { useEffect, useState, useCallback } from 'react'
import { Datasets } from '../charts/core/_models'
import { AlarmChartDataType, DailyAlarmChartDataType } from './core/_model'
import { useIntl } from 'react-intl'

function ChartForAlarmCommentModal(alarmSite: any) {
  let site = alarmSite.siteValue
  let date = alarmSite.dateValue
  let alert_type = alarmSite.alert_type
  let [data, setData] = useState<AlarmChartDataType>({ labels: [], datasets: [] })
  const [dailyData, setDailyData] = useState<DailyAlarmChartDataType>({
    hourly: {
      labels: [],
      datasets: [],
    },
    daily: {
      labels: [],
      datasets: [],
    },
  })
  const [isLoading, setLoading] = useState(true)

  const fetchAlarmChartData = useCallback(async () => {
    try {
      setTimeout(async () => {
        const data = await getAlarmModalChartData(site, date, alert_type)
        setData(data)
        setLoading(false)
      }, 2000)
    } catch (error) {
      console.error('Error fetching alarm chart data:', error)
    }
  }, [site, date, alert_type])

  const fetchDailyAlarmChartData = useCallback(async () => {
    try {
      setTimeout(async () => {
        const dailyData = await getDailyAlarmModalChartData(site, date)
        setDailyData(dailyData)
        setLoading(false)
      }, 2000)
    } catch (error) {
      console.error('Error fetching alarm chart data:', error)
    }
  }, [site, date, alert_type])

  useEffect(() => {
    if (site && date && alert_type === 'hourly') {
      fetchAlarmChartData()
    }
    if (site && date && alert_type === 'daily') {
      fetchDailyAlarmChartData()
    }
  }, [fetchAlarmChartData, fetchDailyAlarmChartData, site, date, alert_type])

  const intl = useIntl()
  if (alert_type === 'daily') {
    data = dailyData.hourly
  }

  const chartData = {
    series:
      data.datasets && data.datasets.length > 0
        ? data.datasets.map((dataset: Datasets) => ({
            name: dataset.name,
            data: dataset.data,
          }))
        : [],
    options: {
      chart: {
        zoom: {
          enabled: true,
        },
        dropShadow: {
          enabled: true,
          color: '#000',
          top: 18,
          left: 7,
          blur: 10,
          opacity: 0.2,
        },
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 1,
      },
      stroke: {
        curve: 'smooth' as const,
      },
      onItemClick: {
        toggleDataSeries: true,
      },
      onItemHover: {
        highlightDataSeries: true,
      },
      xaxis: {
        categories: data.labels,
        tickAmount: 6,
        labels: {
          style: {
            fontSize: '12px',
          },
        },
        axisTicks: {
          show: true,
          borderType: 'solid',
          color: '#78909C',
          height: 6,
          offsetX: 0,
          offsetY: 0,
        },
      },
      height: 350,
    },
  }

  const getDailyValues = () => {
    const dailyDatasets = dailyData.daily.datasets
    if (!dailyDatasets || dailyDatasets.length === 0) {
      return { totalConsumption: '0', averageValue: '0' }
    }

    const dailyValue = dailyDatasets.find(
      (dataset) => dataset.name !== 'Average' && dataset.name !== 'Threshold'
    )
    const averageValue = dailyDatasets.find((dataset) => dataset.name === 'Average')
    const thresholdValue = dailyDatasets.find((dataset) => dataset.name === 'Threshold')

    return {
      totalConsumption: dailyValue?.data[0]?.toString() || '0',
      averageValue: averageValue?.data[0]?.toString() || '0',
      thresholdValue: thresholdValue?.data[0]?.toString() || '0',
    }
  }

  const { totalConsumption, averageValue, thresholdValue } = getDailyValues()

  return (
    <div>
      {isLoading ? (
        <div style={{ textAlign: 'center' }}>
          {intl.formatMessage({ id: 'LOADING' })}...
          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
        </div>
      ) : data.datasets && data.datasets.length > 0 ? (
        <>
          <ReactApexChart
            options={chartData.options}
            series={chartData.series}
            //@ts-ignore
            height={chartData.options.height}
            type={'line'}
          />

          {alert_type === 'daily' && (
            <div className='row mt-4'>
              <div className='col-md-4 mb-3'>
                <div className='card border-0 bg-light'>
                  <div className='card-body text-center py-3'>
                    <h6 className='text-muted mb-1'>
                      {intl.formatMessage({ id: 'ALARMS.DAILY_TOTAL_CONSUMPTION' }) ||
                        'Daily Total Consumption'}
                    </h6>
                    <h4 className='text-primary fw-bold mb-0'>
                      {totalConsumption} <small className='text-muted'>kWh</small>
                    </h4>
                  </div>
                </div>
              </div>
              <div className='col-md-4 mb-3'>
                <div className='card border-0 bg-light'>
                  <div className='card-body text-center py-3'>
                    <h6 className='text-muted mb-1'>
                      {intl.formatMessage({ id: 'ALARMS.AVERAGE_VALUE' }) || 'Average Value'}
                    </h6>
                    <h4 className='text-success fw-bold mb-0'>
                      {averageValue} <small className='text-muted'>kWh</small>
                    </h4>
                  </div>
                </div>
              </div>
              <div className='col-md-4 mb-3'>
                <div className='card border-0 bg-light'>
                  <div className='card-body text-center py-3'>
                    <h6 className='text-muted mb-1'>
                      {intl.formatMessage({ id: 'ALARMS.THRESHOLD_VALUE' }) || 'Threshold Value'}
                    </h6>
                    <h4 className='text-warning fw-bold mb-0'>
                      {thresholdValue} <small className='text-muted'>kWh</small>
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <></>
      )}
    </div>
  )
}

export default ChartForAlarmCommentModal
